import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatMoney, todayISO } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import {
  Target, UserPlus, Pencil, Trash2, Save, X, Search, Filter, Phone,
  MessageSquare, MapPin, Mail, Plus, CalendarClock, ArrowRight, CheckCircle2,
  AlertTriangle, ClipboardList, Package,
} from "lucide-react";

const STATUS_UI = {
  new:                    { cls: "bg-gray-100 text-gray-700",     label: "New" },
  contacted:              { cls: "bg-[#DBEAFE] text-[#1D4ED8]",   label: "Contacted" },
  quoted:                 { cls: "bg-[#FEF3C7] text-[#B45309]",   label: "Quoted" },
  appointment_scheduled:  { cls: "bg-[#F3E8FF] text-[#7E22CE]",   label: "Appointment" },
  negotiating:            { cls: "bg-[#FEF3C7] text-[#B45309]",   label: "Negotiating" },
  won:                    { cls: "bg-[#DCFCE7] text-[#15803D]",   label: "Won" },
  lost:                   { cls: "bg-[#FEE2E2] text-[#B91C1C]",   label: "Lost" },
  on_hold:                { cls: "bg-gray-100 text-gray-500",     label: "On hold" },
};

const SOURCES = [
  { key: "walk_in",    label: "Walk-in" },
  { key: "referral",   label: "Referral" },
  { key: "cold_call",  label: "Cold call" },
  { key: "whatsapp",   label: "WhatsApp" },
  { key: "website",    label: "Website" },
  { key: "other",      label: "Other" },
];

function LeadForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial || {
      name: "", phone: "", email: "", address: "", gst_no: "",
      source: "walk_in", status: "new", notes: "",
      preferred_brands: [], preferred_sizes: [],
      expected_monthly_qty: "", next_action_date: todayISO(),
    }
  );
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      if (initial?.id) await api.patch(`/leads/${initial.id}`, form);
      else await api.post("/leads", form);
      toast.success(initial?.id ? "Lead updated" : "Lead added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-sm sm:rounded-sm w-full sm:max-w-2xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="lead-form-modal">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="small-label text-gray-500">Sales & Marketing</div>
            <div className="font-heading font-black text-xl">{initial?.id ? "Edit lead" : "New lead"}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-sm"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="small-label block mb-1.5">Name / Business</label>
              <input className="input-lg" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                autoFocus data-testid="lead-name" />
            </div>
            <div>
              <label className="small-label block mb-1.5">Phone</label>
              <input className="input-lg" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="lead-phone" />
            </div>
            <div>
              <label className="small-label block mb-1.5">Email</label>
              <input className="input-lg" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="lead-email" />
            </div>
            <div className="col-span-2">
              <label className="small-label block mb-1.5">Address</label>
              <input className="input-lg" value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="lead-address" />
            </div>
            <div>
              <label className="small-label block mb-1.5">GST No. (optional)</label>
              <input className="input-lg" value={form.gst_no}
                onChange={(e) => setForm({ ...form, gst_no: e.target.value })} />
            </div>
            <div>
              <label className="small-label block mb-1.5">Source</label>
              <select className="input-lg" value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}>
                {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="small-label block mb-1.5">Expected monthly qty</label>
              <input className="input-lg" placeholder="e.g. 20 cyl of 19kg"
                value={form.expected_monthly_qty}
                onChange={(e) => setForm({ ...form, expected_monthly_qty: e.target.value })} />
            </div>
            <div>
              <label className="small-label block mb-1.5">Next action date</label>
              <input type="date" className="input-lg" value={form.next_action_date}
                onChange={(e) => setForm({ ...form, next_action_date: e.target.value })}
                data-testid="lead-next-date" />
            </div>
            <div>
              <label className="small-label block mb-1.5">Preferred brands (comma sep.)</label>
              <input className="input-lg" placeholder="HP, Bharat"
                value={(form.preferred_brands || []).join(", ")}
                onChange={(e) => setForm({ ...form, preferred_brands: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            </div>
            <div>
              <label className="small-label block mb-1.5">Preferred sizes (comma sep.)</label>
              <input className="input-lg" placeholder="19kg, 47.5kg"
                value={(form.preferred_sizes || []).join(", ")}
                onChange={(e) => setForm({ ...form, preferred_sizes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            </div>
            <div className="col-span-2">
              <label className="small-label block mb-1.5">Notes</label>
              <textarea className="input-lg min-h-[70px] py-2" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="h-11 px-5 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary h-11 px-6 flex items-center gap-2" data-testid="lead-save">
            <Save size={16} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActivityModal({ lead, onClose, onSaved }) {
  const [form, setForm] = useState({
    mode: "call", comment: "", status: lead.status,
    next_action_date: "", appointment_at: "",
    quoted_items: lead.quoted_items_latest?.length
      ? lead.quoted_items_latest.map((q) => ({ ...q }))
      : [{ brand: "", size: "", filled_qty: "", rate: "" }],
    lost_reason: "",
  });
  const [busy, setBusy] = useState(false);
  const setQ = (i, patch) => setForm({ ...form, quoted_items: form.quoted_items.map((q, idx) => idx === i ? { ...q, ...patch } : q) });
  const addQ = () => setForm({ ...form, quoted_items: [...form.quoted_items, { brand: "", size: "", filled_qty: "", rate: "" }] });
  const rmQ = (i) => setForm({ ...form, quoted_items: form.quoted_items.filter((_, idx) => idx !== i) });
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        mode: form.mode,
        comment: form.comment,
        status: form.status,
        next_action_date: form.next_action_date || null,
        appointment_at: form.appointment_at || null,
        quoted_items: form.quoted_items
          .filter((q) => q.brand && q.size && (Number(q.filled_qty) || Number(q.rate)))
          .map((q) => ({ brand: q.brand, size: q.size, filled_qty: Number(q.filled_qty) || 0, rate: Number(q.rate) || 0 })),
        lost_reason: form.status === "lost" ? form.lost_reason : "",
      };
      await api.post(`/leads/${lead.id}/activity`, payload);
      toast.success("Activity logged");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-sm sm:rounded-sm w-full sm:max-w-2xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="activity-modal">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="small-label text-gray-500">Log activity</div>
            <div className="font-heading font-black text-xl truncate">{lead.name}</div>
            {lead.phone && <div className="text-xs text-gray-500 mt-0.5">{lead.phone}</div>}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-sm"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="small-label block mb-1.5">How was it done?</label>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { k: "call", l: "Call", i: Phone },
                { k: "whatsapp", l: "WhatsApp", i: MessageSquare },
                { k: "personal_visit", l: "Personal visit", i: MapPin },
                { k: "email", l: "Email", i: Mail },
                { k: "other", l: "Other", i: ClipboardList },
              ].map((m) => (
                <button key={m.k} type="button"
                  onClick={() => setForm({ ...form, mode: m.k })}
                  className={`h-10 px-3 text-xs font-semibold rounded-sm border flex items-center gap-1.5 ${form.mode === m.k ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
                  data-testid={`activity-mode-${m.k}`}>
                  <m.i size={13} /> {m.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="small-label block mb-1.5">Outcome / notes</label>
            <textarea className="input-lg min-h-[80px] py-2" value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              placeholder="Met the owner, discussed rates…" data-testid="activity-comment" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="small-label block mb-1.5">New status</label>
              <select className="input-lg" value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                data-testid="activity-status">
                {Object.keys(STATUS_UI).map((s) => (
                  <option key={s} value={s}>{STATUS_UI[s].label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="small-label block mb-1.5">Next action date</label>
              <input type="date" className="input-lg" value={form.next_action_date}
                onChange={(e) => setForm({ ...form, next_action_date: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className="small-label block mb-1.5">Appointment / visit at (optional)</label>
              <input type="datetime-local" className="input-lg" value={form.appointment_at}
                onChange={(e) => setForm({ ...form, appointment_at: e.target.value })} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="small-label">Rate quoted (optional)</div>
              <button onClick={addQ} className="text-xs font-semibold flex items-center gap-1" data-testid="activity-quote-add">
                <Plus size={12} /> Add row
              </button>
            </div>
            <div className="space-y-2">
              {form.quoted_items.map((q, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`activity-quote-${i}`}>
                  <input placeholder="Brand" className="input-lg col-span-3" value={q.brand}
                    onChange={(e) => setQ(i, { brand: e.target.value })} />
                  <input placeholder="Size" className="input-lg col-span-3" value={q.size}
                    onChange={(e) => setQ(i, { size: e.target.value })} />
                  <input type="number" placeholder="Qty" className="input-lg col-span-2 text-right data-num" value={q.filled_qty}
                    onChange={(e) => setQ(i, { filled_qty: e.target.value })} />
                  <input type="number" step="0.01" placeholder="Rate ₹" className="input-lg col-span-3 text-right data-num" value={q.rate}
                    onChange={(e) => setQ(i, { rate: e.target.value })} />
                  <button onClick={() => rmQ(i)} className="col-span-1 text-gray-400 hover:text-[#B91C1C] p-2 disabled:opacity-30"
                    disabled={form.quoted_items.length === 1}>
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {form.status === "lost" && (
            <div>
              <label className="small-label block mb-1.5">Reason for losing</label>
              <input className="input-lg" value={form.lost_reason}
                onChange={(e) => setForm({ ...form, lost_reason: e.target.value })}
                placeholder="Price too high, went to competitor, budget cut…" />
            </div>
          )}

          {lead.history?.length > 0 && (
            <details className="card-flat p-3 text-xs" data-testid="activity-history">
              <summary className="cursor-pointer font-semibold text-gray-700">Previous activities ({lead.history.length})</summary>
              <div className="mt-2 space-y-2 max-h-48 overflow-auto">
                {[...lead.history].reverse().map((h, i) => (
                  <div key={i} className="border-l-2 border-gray-200 pl-3 py-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{h.date}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-600 font-bold">{(h.mode || "").replace("_", " ")}</span>
                      <span className="text-gray-400 ml-auto">{h.by_name || h.by_email}</span>
                    </div>
                    <div className="text-gray-700">{h.comment || <em className="text-gray-400">no comment</em>}</div>
                    {h.appointment_at && <div className="text-[#7E22CE]">Appt: {h.appointment_at}</div>}
                    {h.next_action_date && <div className="text-gray-500">next: {h.next_action_date}</div>}
                    {(h.quoted_items || []).length > 0 && (
                      <div className="text-gray-500">
                        Quoted: {(h.quoted_items || []).map((q) => `${q.brand} ${q.size} @ ₹${q.rate}`).join(", ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
        <div className="p-5 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="h-11 px-5 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary h-11 px-6 flex items-center gap-2" data-testid="activity-save">
            <Save size={16} /> {busy ? "Saving…" : "Log activity"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Sales() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activityFor, setActivityFor] = useState(null);
  const [tab, setTab] = useState("leads"); // leads | appointments | quick-add

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads", { params: { status, search } });
      setRows(data.rows || []);
      setSummary(data.summary || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [search]);

  const remove = async (l) => {
    if (!window.confirm(`Delete lead "${l.name}"?`)) return;
    try { await api.delete(`/leads/${l.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const convert = async (l) => {
    if (!window.confirm(`Convert "${l.name}" into a customer? A new party will be created and the lead marked as Won.`)) return;
    try {
      const { data } = await api.post(`/leads/${l.id}/convert`);
      toast.success(`Converted → customer created (${data.party.name})`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const appointments = useMemo(() => rows.filter((r) => r.appointment_at), [rows]);
  const today = todayISO();

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Target size={20} className="text-black" />
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">Sales & Marketing</h2>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">Leads · Appointments · Conversions</span>
        <button onClick={() => { setEditing(null); setShowLeadForm(true); }}
          className="ml-auto btn-primary h-11 px-5 flex items-center gap-2" data-testid="new-lead-btn">
          <UserPlus size={16} /> New lead
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Total leads</div>
          <div className="data-num text-3xl mt-1" data-testid="kpi-total">{summary.total || 0}</div>
        </div>
        <div className="card-flat p-5 bg-gray-50">
          <div className="small-label text-gray-700">New</div>
          <div className="data-num text-3xl mt-1" data-testid="kpi-new">{summary.new || 0}</div>
        </div>
        <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A]">
          <div className="small-label text-[#B45309]">Quoted / Negotiating</div>
          <div className="data-num text-3xl mt-1 text-[#B45309]" data-testid="kpi-quoted">{summary.quoted || 0}</div>
        </div>
        <div className="card-flat p-5 bg-[#F3E8FF] border-[#E9D5FF]">
          <div className="small-label text-[#7E22CE] flex items-center gap-1.5"><CalendarClock size={12} /> Appointment</div>
          <div className="data-num text-3xl mt-1 text-[#7E22CE]" data-testid="kpi-appt">{summary.appointment || 0}</div>
        </div>
        <div className="card-flat p-5 bg-[#DCFCE7] border-[#86EFAC]">
          <div className="small-label text-[#15803D] flex items-center gap-1.5"><CheckCircle2 size={12} /> Won</div>
          <div className="data-num text-3xl mt-1 text-[#15803D]" data-testid="kpi-won">{summary.won || 0}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="inline-flex bg-white border border-gray-300 rounded-sm p-0.5">
        <button onClick={() => setTab("leads")}
          className={`h-9 px-4 text-xs font-semibold rounded-sm ${tab === "leads" ? "bg-black text-white" : "text-gray-600 hover:text-black"}`}
          data-testid="sales-tab-leads">Leads</button>
        <button onClick={() => setTab("appointments")}
          className={`h-9 px-4 text-xs font-semibold rounded-sm ${tab === "appointments" ? "bg-black text-white" : "text-gray-600 hover:text-black"}`}
          data-testid="sales-tab-appointments">Appointments</button>
      </div>

      {tab === "leads" && (
        <>
          {/* Controls */}
          <div className="card-flat p-4 flex items-center gap-3 flex-wrap">
            <Filter size={14} className="text-gray-500" />
            <select className="input-lg h-9 w-auto" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="status-filter">
              <option value="all">All statuses</option>
              {Object.keys(STATUS_UI).map((s) => (<option key={s} value={s}>{STATUS_UI[s].label}</option>))}
            </select>
            {summary.overdue > 0 && (
              <span className="text-xs text-[#B91C1C] font-semibold flex items-center gap-1">
                <AlertTriangle size={12} /> {summary.overdue} overdue follow-ups
              </span>
            )}
            <div className="relative ml-auto">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input className="input-lg h-9 pl-8 w-56" placeholder="Search name / phone…"
                value={search} onChange={(e) => setSearch(e.target.value)} data-testid="lead-search" />
            </div>
          </div>

          {/* Leads table */}
          <div className="card-flat overflow-auto">
            <table className="table-dense w-full">
              <thead>
                <tr>
                  <th className="text-left">Name</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">Contact</th>
                  <th className="text-left">Next action</th>
                  <th className="text-left">Latest quote</th>
                  <th className="text-left">Assigned</th>
                  <th className="text-right"></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan="7" className="py-8 text-center text-sm text-gray-400">Loading…</td></tr>}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan="7" className="py-10 text-center text-sm text-gray-400">
                    <Target size={26} className="inline-block text-gray-300 mb-1" />
                    <div>No leads yet. Click &quot;New lead&quot; to add one.</div>
                  </td></tr>
                )}
                {rows.map((l) => {
                  const s = STATUS_UI[l.status] || STATUS_UI.new;
                  const overdue = l.next_action_overdue;
                  return (
                    <tr key={l.id} className={overdue ? "bg-[#FEE2E2]/40" : ""} data-testid={`lead-row-${l.id}`}>
                      <td>
                        <div className="font-semibold text-sm">{l.name}</div>
                        {l.address && <div className="text-[10px] text-gray-500 truncate max-w-[220px]">{l.address}</div>}
                        {l.converted_party_id && (
                          <Link to={`/parties/${l.converted_party_id}`} className="text-[10px] text-[#15803D] hover:underline flex items-center gap-1">
                            <ArrowRight size={9} /> customer created
                          </Link>
                        )}
                      </td>
                      <td>
                        <span className={`inline-block px-2 py-0.5 text-xs rounded-sm font-semibold ${s.cls}`}>{s.label}</span>
                        {overdue && <div className="text-[10px] text-[#B91C1C] mt-0.5 font-semibold">next action overdue</div>}
                      </td>
                      <td className="text-xs">
                        {l.phone && <div className="flex items-center gap-1"><Phone size={10} /> {l.phone}</div>}
                        {l.email && <div className="flex items-center gap-1 text-gray-500"><Mail size={10} /> {l.email}</div>}
                      </td>
                      <td className="data-num text-xs">
                        {l.next_action_date || <span className="text-gray-400">—</span>}
                        {l.appointment_at && <div className="text-[10px] text-[#7E22CE] font-semibold">Appt: {l.appointment_at}</div>}
                      </td>
                      <td className="text-[11px]">
                        {(l.quoted_items_latest || []).slice(0, 2).map((q, i) => (
                          <div key={i}>{q.brand} {q.size} · <b>₹ {formatMoney(q.rate)}</b> × {q.filled_qty}</div>
                        ))}
                        {(l.quoted_items_latest || []).length > 2 && (
                          <div className="text-gray-500">+{l.quoted_items_latest.length - 2} more</div>
                        )}
                        {!(l.quoted_items_latest || []).length && <span className="text-gray-400">—</span>}
                      </td>
                      <td className="text-xs text-gray-500">{l.created_by_email || "—"}</td>
                      <td className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => setActivityFor(l)}
                            className="btn-primary h-8 px-3 text-xs flex items-center gap-1.5"
                            data-testid={`log-activity-${l.id}`}>
                            <ClipboardList size={12} /> Log
                          </button>
                          {!l.converted_party_id && l.status !== "lost" && (
                            <button onClick={() => convert(l)}
                              className="h-8 px-2 text-xs font-semibold border border-[#15803D] text-[#15803D] rounded-sm hover:bg-[#DCFCE7] flex items-center gap-1"
                              title="Convert to customer"
                              data-testid={`convert-${l.id}`}>
                              <Package size={11} /> Convert
                            </button>
                          )}
                          <button onClick={() => { setEditing(l); setShowLeadForm(true); }}
                            className="text-gray-500 hover:text-black p-1" title="Edit"
                            data-testid={`edit-lead-${l.id}`}><Pencil size={13} /></button>
                          <button onClick={() => remove(l)}
                            className="text-gray-400 hover:text-[#B91C1C] p-1" title="Delete"
                            data-testid={`del-lead-${l.id}`}><Trash2 size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "appointments" && (
        <div className="card-flat overflow-auto" data-testid="appointments-panel">
          <div className="p-4 border-b border-gray-200 small-label text-gray-500 flex items-center justify-between">
            <span>Scheduled visits / appointments</span>
            <span className="text-xs">{appointments.length} scheduled</span>
          </div>
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Appointment</th>
                <th className="text-left">Lead</th>
                <th className="text-left">Contact</th>
                <th className="text-left">Status</th>
                <th className="text-right"></th>
              </tr>
            </thead>
            <tbody>
              {appointments.length === 0 && (
                <tr><td colSpan="5" className="py-10 text-center text-sm text-gray-400">
                  <CalendarClock size={26} className="inline-block text-gray-300 mb-1" />
                  <div>No appointments yet. Log an activity with an appointment date on any lead.</div>
                </td></tr>
              )}
              {appointments.sort((a, b) => (a.appointment_at || "").localeCompare(b.appointment_at || "")).map((l) => {
                const s = STATUS_UI[l.status] || STATUS_UI.new;
                return (
                  <tr key={l.id} data-testid={`appt-row-${l.id}`}>
                    <td className="data-num text-xs font-semibold">{l.appointment_at}</td>
                    <td>
                      <div className="font-semibold text-sm">{l.name}</div>
                      {l.address && <div className="text-[10px] text-gray-500">{l.address}</div>}
                    </td>
                    <td className="text-xs">
                      {l.phone && <a href={`tel:${l.phone}`} className="text-black hover:underline flex items-center gap-1"><Phone size={10} /> {l.phone}</a>}
                    </td>
                    <td>
                      <span className={`inline-block px-2 py-0.5 text-xs rounded-sm font-semibold ${s.cls}`}>{s.label}</span>
                    </td>
                    <td className="text-right">
                      <button onClick={() => setActivityFor(l)} className="btn-primary h-8 px-3 text-xs flex items-center gap-1.5" data-testid={`appt-log-${l.id}`}>
                        <ClipboardList size={12} /> Log
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showLeadForm && (
        <LeadForm initial={editing} onClose={() => { setShowLeadForm(false); setEditing(null); }}
          onSaved={() => { setShowLeadForm(false); setEditing(null); load(); }} />
      )}
      {activityFor && (
        <ActivityModal lead={activityFor} onClose={() => setActivityFor(null)}
          onSaved={() => { setActivityFor(null); load(); }} />
      )}
    </div>
  );
}
